import React from "react"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

const withSlash = (path) =>
  path.endsWith("/") || /\.[a-z0-9]+$/i.test(path) ? path : `${path}/`

const SEO = ({
  title,
  description,
  pathname,
  image,
  article = false,
  schema,
  noindex = false,
}) => {
  const { site } = useStaticQuery(
    graphql`
      query SeoQuery {
        site {
          siteMetadata {
            title
            description
            siteUrl
            image
          }
        }
      }
    `
  )

  const meta = site.siteMetadata
  // Webpack inlines small imported images as data: URIs, which cannot be a
  // share image (it rendered as "https://www.hotsnail.com.audata:image/...").
  // Fall back to the site default for those.
  const usableImage = image && !image.startsWith("data:") ? image : null
  const seo = {
    title: title || meta.title,
    description: description || meta.description,
    // Match the canonical, which always ends in a slash. Most pages pass
    // pathname without one, so og:url named the redirecting URL.
    url: `${meta.siteUrl}${withSlash(pathname || "/")}`,
    image: usableImage
      ? usableImage.startsWith("http")
        ? usableImage
        : `${meta.siteUrl}${usableImage}`
      : `${meta.siteUrl}${meta.image}`,
  }

  return (
    <Helmet>
      <html lang="en" />
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      {/* No <link rel="canonical"> here: gatsby-plugin-canonical-urls emits one
          from the real page path. Emitting a second one caused every category
          page to ship two conflicting canonicals. */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      <link rel="icon" type="image/png" href="/favicon.ico" />

      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(schema)
              ? { "@context": "https://schema.org", "@graph": schema }
              : { "@context": "https://schema.org", ...schema }
          )}
        </script>
      )}
    </Helmet>
  )
}

export default SEO
